import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  manageContent,
  shouldHideMarkCompleteForMissingProgramTrack,
  matchesTrainingPathPattern,
} from 'pages/ResourcesContent/helpers/articleContentHelpers'
import { LAYOUT_WITH_ANCHOR_NAVIGATION } from './pageLayouts'
import {
  AnchoredArticleWrapper,
  AnchoredContentWrapper,
  AnchoredPageWrapper,
  AnchorNavigationWrapper,
  BannerSpacer,
  ContentWrapper,
  GrayBackground,
  PageWrapper,
} from './component.styles'

import HighSchoolPreviewBanner from 'components/HighSchoolPreviewBanner'
import MarkCompleteSection from 'components/MarkCompleteSection'
import CoursePreviewBannerContainer from 'components/CoursePreviewBanner'
import SiteSelector, {
  DefaultSiteSelectorWrapper,
  SiteSelectorK8WrapperDark,
} from 'components/SiteSelector'
import ProgressIndicator from 'components/ProgressIndicator'
import {
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
} from 'utils/routing/routeHelpers'

import AnchoredHeaderBodyCopy from 'experienceComponents/AnchoredHeaderBodyCopy'
import ArticleHeaderBodyCopy from 'experienceComponents/ArticleHeaderBodyCopy'
import AnchorNavigation from 'experienceComponents/AnchorNavigation'
import AssetDescription from 'experienceComponents/AssetDescription'
import BookDescription from 'experienceComponents/BookDescription'
import ContentLandingBanner from 'experienceComponents/ContentLandingBanner'
import H3LinkList from 'experienceComponents/H3LinkList'
import HeaderBodyCopy from 'experienceComponents/HeaderBodyCopy'
import InlineTranslation from 'components/InlineTranslation'
import LanguageToggle from 'experienceComponents/LanguageToggle'
import MultilingualPdfSelector from 'components/MultilingualPdfSelector'
import TextLinkWithFileIcon from 'components/TextLinkWithFileIcon'
import VideoFullWidth from 'experienceComponents/VideoFullWidth'
import ImageDescription from 'experienceComponents/ImageDescription'
import FlexWidthImage from 'components/FlexWidthImage'
import MediaBodyCopyComponent from 'experienceComponents/MediaBodyCopy'
import ResourcesContentAccordionContainer from 'components/ResourcesContentAccordion'

const ContentComponents = {
  accordion: ResourcesContentAccordionContainer,
  anchor: AnchoredHeaderBodyCopy,
  assetDescription: AssetDescription,
  book: BookDescription,
  category: H3LinkList,
  headerBodyCopy: HeaderBodyCopy,
  image: FlexWidthImage,
  imageBodyCopy: ImageDescription,
  inlineTranslation: InlineTranslation,
  langPdf: MultilingualPdfSelector,
  lang: LanguageToggle,
  textLinkIcon: TextLinkWithFileIcon,
  video: VideoFullWidth,
  mediaBodyCopy: MediaBodyCopyComponent,
}

const NonAnchoredContentComponents = {
  ...ContentComponents,
  headerBodyCopy: ArticleHeaderBodyCopy,
}

export const groupArticlesByAnchors = (contents = []) => {
  let insideBoundary = false

  return contents.reduce((result, content) => {
    const { name, data } = content
    if (name === 'lang') {
      const { boundary } = data || {}
      if (boundary) {
        insideBoundary = true
        return [...result, [content]]
      }
      insideBoundary = false
    }

    if (name === 'anchor' && !insideBoundary) {
      return [...result, [content]]
    }

    const lastIndex = result.length - 1
    const currentIndex = lastIndex < 0 ? 0 : lastIndex
    const currentGroup = result[currentIndex] || []
    const updatedCurrentGroup = [...currentGroup, content]
    const resultsLessLastItem = result.slice(0, result.length - 1)

    return [...resultsLessLastItem, updatedCurrentGroup]
  }, [])
}

export const ResourcesContentV3 = ({
  anchorNavigation,
  content,
  contentGroupCode,
  courseNodeId,
  coursePointer,
  currentLocale,
  getEntryWithNewLocale,
  instructorDataKey,
  isHsPreview,
  landingBanner,
  markComplete,
  numberOfCompletedTrainingPrograms,
  pageLayout,
  productTabTitle,
  refreshInstructorWithGet,
  shouldShowSiteSelector,
  siteId,
  userContextManager,
}) => {
  useEffect(() => {
    const { title } = landingBanner || {}
    const fullTitle = `${productTabTitle} | ${title}`

    if (title && document.title !== fullTitle) {
      document.title = fullTitle
    }
  }, [landingBanner, productTabTitle])

  const getContentComponent = useCallback(
    (contentItem, index, nonAnchor) => {
      const { name, data: componentProps } = contentItem || {}
      const Components = nonAnchor
        ? NonAnchoredContentComponents
        : ContentComponents
      const ContentComponent = Components[name] || null

      if (ContentComponent === null) {
        return null
      }

      if (name === 'image') {
        return (
          <ContentComponent
            key={`content-${index}-${name}`}
            {...componentProps}
          />
        )
      }

      return (
        <ContentComponent
          key={`content-${index}-${name}`}
          {...componentProps}
          getEntryWithNewLocale={getEntryWithNewLocale}
          locale={currentLocale}
        />
      )
    },
    [currentLocale, getEntryWithNewLocale],
  )

  const getAnchoredContentComponents = useCallback(() => {
    const articleGroups = groupArticlesByAnchors(content || [])

    return articleGroups.map((articleGroup, wrapperIndex) =>
      manageContent(
        articleGroup,
        ContentComponents,
        getContentComponent,
        contentComponents => (
          <AnchoredArticleWrapper key={`anchorGroup-${wrapperIndex}`}>
            {contentComponents}
          </AnchoredArticleWrapper>
        ),
      ),
    )
  }, [content, getContentComponent])

  const getContentComponents = useCallback(
    () =>
      manageContent(
        content,
        ContentComponents,
        getContentComponent,
        contentComponents => contentComponents,
      ),
    [content, getContentComponent],
  )

  const getPageContent = () => {
    const { numberTrainingsComplete } = markComplete || {}
    const isK5 = isK5Url()
    const isMSUrl = isMiddleSchoolUrl()

    const shouldHideMarkComplete = shouldHideMarkCompleteForMissingProgramTrack(
      userContextManager,
      isK5,
      isMSUrl,
    )

    const markCompletePanel =
      !!markComplete &&
      !shouldHideMarkComplete &&
      courseNodeId &&
      contentGroupCode &&
      instructorDataKey ? (
        <MarkCompleteSection
          {...markComplete}
          contentGroupCode={contentGroupCode}
          courseNodeId={courseNodeId}
          coursePointer={coursePointer}
          instructorDataKey={instructorDataKey}
          refreshInstructorWithGet={refreshInstructorWithGet}
          siteId={siteId}
        />
      ) : null

    if (pageLayout === LAYOUT_WITH_ANCHOR_NAVIGATION) {
      return (
        <GrayBackground>
          <AnchoredPageWrapper>
            <AnchorNavigationWrapper>
              <AnchorNavigation links={anchorNavigation} />
            </AnchorNavigationWrapper>
            <AnchoredContentWrapper>
              {!!numberTrainingsComplete &&
                !isHsPreview &&
                !shouldHideMarkComplete && (
                  <ProgressIndicator
                    completed={numberOfCompletedTrainingPrograms}
                    text={numberTrainingsComplete}
                  />
                )}
              {getAnchoredContentComponents()}
              {markCompletePanel}
            </AnchoredContentWrapper>
          </AnchoredPageWrapper>
        </GrayBackground>
      )
    }

    return (
      <GrayBackground>
        <ContentWrapper>
          {getContentComponents()}
          {markCompletePanel}
        </ContentWrapper>
      </GrayBackground>
    )
  }

  const SiteSelectorWrapper = isHighSchoolUrl()
    ? DefaultSiteSelectorWrapper
    : SiteSelectorK8WrapperDark

  const isK5 = isK5Url()
  const isMSUrl = isMiddleSchoolUrl()

  const shouldShowCoursePreviewBanner =
    matchesTrainingPathPattern() &&
    shouldHideMarkCompleteForMissingProgramTrack(
      userContextManager,
      isK5,
      isMSUrl,
    )

  return (
    <PageWrapper data-testid="resources-content-page">
      {isHsPreview && (
        <>
          <BannerSpacer />
          <HighSchoolPreviewBanner />
        </>
      )}
      {shouldShowCoursePreviewBanner && (
        <CoursePreviewBannerContainer customTopMargin="0" isTraining />
      )}
      {shouldShowSiteSelector && (
        <SiteSelectorWrapper>
          <SiteSelector />
        </SiteSelectorWrapper>
      )}
      <ContentLandingBanner key="landingBanner" {...landingBanner} />
      {getPageContent()}
    </PageWrapper>
  )
}

ResourcesContentV3.propTypes = {
  anchorNavigation: PropTypes.arrayOf(
    PropTypes.shape({
      hash: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ),
  content: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.object.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  contentGroupCode: PropTypes.string,
  courseNodeId: PropTypes.string,
  coursePointer: PropTypes.object,
  currentLocale: PropTypes.string,
  getEntryWithNewLocale: PropTypes.func,
  instructorDataKey: PropTypes.string,
  isHsPreview: PropTypes.bool,
  landingBanner: PropTypes.shape({
    text: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  markComplete: PropTypes.object,
  numberOfCompletedTrainingPrograms: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  pageLayout: PropTypes.string.isRequired,
  productTabTitle: PropTypes.string,
  refreshInstructorWithGet: PropTypes.bool,
  shouldShowSiteSelector: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userContextManager: PropTypes.object,
}

export default ResourcesContentV3
